import type { ComponentPropsWithRef } from 'react'
import { cn } from '../../lib/cn'
import s from './RailItem.module.css'

/**
 * Rangée sélectionnable d'un rail maître-détail — report de `.acc-li` du prototype validé,
 * partagée par le rail de `Comptes équipes` et celui de `Planning` (tous deux dans
 * `.plan-rail`).
 *
 * Pas de primitive : un `<button>` natif porte déjà le rôle, le focus et l'activation au
 * clavier (corollaire de la règle 3, comme `Button`).
 *
 * Elle porte le **cadre et les trois états** (repos, survol, sélectionné), rien d'autre :
 * le contenu — pastille d'équipe, nom, identifiant, marché — appartient à l'écran, comme
 * les colonnes appartiennent à l'écran dans `DataTable`. Le conteneur du rail non plus
 * n'est pas ici : sa largeur, son filet et son défilement sont de la mise en page d'écran.
 *
 * **L'état sélectionné va au DOM, pas à une classe** — comme l'état actif de `NavItem` :
 *  - `data-selected` pour le style, lu par le seul module CSS ;
 *  - `aria-current="true"` pour l'annonce, sans quoi la sélection n'existe qu'à l'œil.
 *
 * Les deux sont posés ensemble ou pas du tout : c'est une seule vérité, dite deux fois
 * parce que deux publics la lisent.
 */
export interface RailItemProps extends ComponentPropsWithRef<'button'> {
  /** Sélectionné : c'est cette rangée que le panneau de détail affiche. */
  selected?: boolean
}

export function RailItem({ selected = false, className, ...props }: RailItemProps) {
  return (
    <button
      // Jamais `submit` implicite, même défaut que `Button` : un rail vit souvent dans un
      // formulaire de filtres, et une rangée qui le soumettrait serait indétectable.
      type="button"
      className={cn(s.root, className)}
      {...(selected && { 'data-selected': '', 'aria-current': true })}
      {...props}
    />
  )
}
